import { FileKind } from '@core/data-access/models/file-upload.model';

export enum FieldKey {
  Gender = 'gender',
  Title = 'title',
  FirstName = 'firstName',
  LastName = 'lastName',
  Company = 'company',
  Function = 'function',
  Department = 'department',
  OccupationalGroup = 'occupationalGroup',
  Country = 'country',
  Street = 'street',
  ZipCode = 'zipCode',
  City = 'city',
  Telephone = 'telephone',
  VatNumber = 'vatNumber',
  Email = 'email',
  MailConfirm = 'mailConfirm',
  Address = 'address',
  SaveAddress = 'saveAddress',
  DateOfBirth = 'dateOfBirth',
  Password = 'password',
  VerifyPassword = 'verifyPassword',
  CreateAccount = 'createAccount',
  Newsletter = 'newsletter',
  CreateAccountButton = 'createAccountButton',
  FairCatalogue = 'fairCatalogue',
  DifferentBillingAddress = 'differentBillingAddress',
  SendToOwner = 'sendToOwner',
  Disclaimer = 'disclaimer',
  DisclaimerOptional = 'disclaimerOptional',
  GDPR = 'GDPR',
  CV = 'CV'
}

export type QuestionnaireFieldKey = string;

export enum FieldType {
  TextInput = 'text',
  NumberInput = 'number',
  DateInput = 'dateInput',
  Dropdown = 'dropdown',
  Checkbox = 'checkbox',
  Radio = 'radio',
  Email = 'email',
  Password = 'password',
  FileUpload = 'fileUpload',
  SubmitButton = 'submitButton'
}

export enum OptionKey {
  SaveBillingAddress = 'saveBillingAddress',
  CreateAccount = 'createAccount',
  IsNewsletterChecked = 'isNewsletterChecked',
  NewsletterOptIn = 'newsletterOptIn',
  NewsletterOptOut = 'newsletterOptOut',
  IsFairCatalogChecked = 'isFairCatalogChecked',
  DifferentBillingAddress = 'differentBillingAddress',
  ToOwner = 'toOwner',
  DisclaimerConfirmation = 'disclaimerConfirmation',
  DisclaimerOptionalConfirmation = 'disclaimerOptionalConfirmation',
  TradeConditions = 'tradeConditions',
  GDPRConsent = 'GDPRConsent'
}

export interface BaseDynamicFormField<T = FieldKey | QuestionnaireFieldKey> {
  key: T;
  type: FieldType;
  label: string;
  placeholder?: string;
  order: number;
  isRequired: boolean;
  isHidden: boolean;
  isDisabled: boolean;
  isQuestionnaireField: boolean;
  columns: ColumnSizes;
}

export interface ColumnSizes {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

export interface BaseQuestionnaireDynamicFormField extends BaseDynamicFormField<string> {
  placeholder: string;
  previousValueId: number | null;
  selectionLimit: number | null;
  textLength: number | null;
}

export interface TextInputField extends BaseDynamicFormField {
  minLengthValidation?: number;
  maxLengthValidation?: number;
}

export type NumberInputField = BaseDynamicFormField;

export type DateInputField = BaseDynamicFormField;

export interface DropdownField extends BaseDynamicFormField {
  options: DropdownOption[];
}

export interface QuestionnaireDropdownField extends BaseQuestionnaireDynamicFormField {
  options: CheckboxOption[];
}

export interface DropdownOption {
  key: string | number;
  value: string;
  isDisabled: boolean;
}

export interface CheckboxField extends BaseDynamicFormField {
  options: CheckboxOption[];
}

export interface QuestionnaireCheckboxField extends BaseQuestionnaireDynamicFormField {
  options: CheckboxOption[];
}

export interface PrivacyPolicyCheckboxField extends CheckboxField {
  text: {
    translation: string;
    translationKey: string;
    position: 'prepend' | 'append';
  };
}

export interface CheckboxOption {
  key: CheckboxOptionKey;
  label: string;
  value: boolean;
  isRequired: boolean;
  isDisabled: boolean;
  icon?: string;
}

export type CheckboxOptionKey = OptionKey | string | number;

export type CheckboxUncheckGuards = Record<FieldKey | QuestionnaireFieldKey, CheckboxOptionUncheckGuards>;

export type CheckboxOptionUncheckGuards = Partial<Record<CheckboxOptionKey, boolean>>;

export interface RadioField extends BaseDynamicFormField {
  options: RadioOption[];
}

export interface QuestionnaireRadioField extends BaseQuestionnaireDynamicFormField {
  options: CheckboxOption[];
}

export interface RadioOption {
  key: string | number;
  value: string;
  isDisabled: boolean;
}

export interface EmailField extends BaseDynamicFormField {
  maxLengthValidation: number;
  emailValidation: boolean;
  emailCaseInsensitiveValidation: boolean;
}

export interface PasswordField extends BaseDynamicFormField {
  minLengthValidation: number;
}

export interface CompanyField extends BaseDynamicFormField {
  maxLengthValidation: number;
}

export interface ZipCodeField extends BaseDynamicFormField {
  maxLengthValidation: number;
  zipCodeValidation: boolean;
}

export interface PhoneNumberField extends BaseDynamicFormField {
  phoneValidation: boolean;
}

export interface FileUploadField extends BaseDynamicFormField {
  tooltip?: string;
  fileKind: FileKind;
  acceptedFileTypes: string;
  maxFileSize: number;
}

export type SubmitButton = BaseDynamicFormField;

export type DynamicFormField =
  | TextInputField
  | NumberInputField
  | DateInputField
  | DropdownField
  | CheckboxField
  | RadioField
  | EmailField
  | PasswordField
  | CompanyField
  | ZipCodeField
  | PhoneNumberField
  | FileUploadField
  | SubmitButton;

export type DynamicFormFields = DynamicFormField[];

export type QuestionnaireDynamicFormFields = BaseQuestionnaireDynamicFormField[];
